import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";

// Importar las credenciales directamente desde supabase.js
const supabaseUrl = 'https://dundlwzoomxcqbulnylj.supabase.co';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR1bmRsd3pvb214Y3FidWxueWxqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQ5MzUxNDAsImV4cCI6MjA2MDUxMTE0MH0.Zhpv_6T5o03HJM65PCfz4QpD41Ehl56ML7qRMDoRfks';
import { Send, ArrowLeft } from "lucide-react";
import { ChatContext } from "./ChatContext";
import { ChatSelector } from "./ChatSelector";
import { useAuth } from "@/components/auth/AuthContext";

export function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [n8nConfig, setN8nConfig] = useState(null);
  const messagesEndRef = useRef(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const [context, setContext] = useState({
    selectedProduct: null,
    selectedCustomer: null,
    selectedRepresentative: null
  });

  useEffect(() => {
    loadN8nConfig();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadN8nConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('n8n_settings')
        .not('n8n_settings', 'is', null)
        .limit(1);

      if (error) throw error;
      
      if (data && data.length > 0) {
        console.log('Configuración n8n cargada:', data[0].n8n_settings);
        setN8nConfig(data[0].n8n_settings);
      }
    } catch (error) {
      console.error('Error loading n8n config:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar la configuración de n8n",
        variant: "destructive"
      });
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const message = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: message }]);

    try {
      // Verificar si hay usuario antes de enviar
      if (!user) {
        throw new Error('Usuario no autenticado');
      }

      // Verificar si tenemos la configuración de n8n
      if (!n8nConfig) {
        throw new Error('Configuración de n8n no disponible');
      }

      console.log('Usuario actual:', user);
      console.log('Credenciales Supabase:', { 
        supabaseUrl: supabaseUrl ? 'Definida' : 'No definida',
        supabaseKey: supabaseKey ? 'Definida' : 'No definida'
      });

              const requestBody = {
          chatInput: message,
          sessionId: `user_${user?.id || 'unknown'}_${Date.now()}`,
          userId: user?.id || 'unknown',
          selectedProduct: context.selectedProduct,
          selectedCustomer: context.selectedCustomer,
          selectedRepresentative: context.selectedRepresentative,
          supabaseUrl: supabaseUrl,
          supabaseKey: supabaseKey
        };
      
      console.log('Body enviado:', requestBody);
      
      const response = await fetch(n8nConfig.n8n_webhook, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-N8N-API-KEY': n8nConfig.n8n_api_key,
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: error.message === 'Usuario no autenticado' 
          ? 'Por favor, inicia sesión para usar el chat.' 
          : error.message === 'Configuración de n8n no disponible'
          ? 'Error de configuración del chat.'
          : 'Lo siento, hubo un error al procesar tu mensaje.' 
      }]);
    }
  };

  // Funciones para gestionar el contexto
  const handleSelectProduct = (product) => {
    setContext(prev => ({ ...prev, selectedProduct: product }));
  };

  const handleSelectCustomer = (customer) => {
    setContext(prev => ({ ...prev, selectedCustomer: customer }));
  };

  const handleSelectRepresentative = (representative) => {
    setContext(prev => ({ ...prev, selectedRepresentative: representative }));
  };

  const handleClearContext = () => {
    setContext({
      selectedProduct: null,
      selectedCustomer: null,
      selectedRepresentative: null
    });
  };

  const handleClearProduct = () => {
    setContext(prev => ({ ...prev, selectedProduct: null }));
  };

  const handleClearCustomer = () => {
    setContext(prev => ({ ...prev, selectedCustomer: null }));
  };

  const handleClearRepresentative = () => {
    setContext(prev => ({ ...prev, selectedRepresentative: null }));
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      <div className="p-4 border-b">
        <Button
          variant="outline"
          onClick={() => window.location.hash = "#/products"}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
      </div>
      
      {/* Selectores de contexto */}
      <div className="p-4 border-b bg-gray-50">
        <div className="flex flex-wrap gap-2">
          <ChatSelector
            type="product"
            onSelect={handleSelectProduct}
            placeholder="Producto"
          />
          <ChatSelector
            type="customer"
            onSelect={handleSelectCustomer}
            placeholder="Cliente"
          />
          <ChatSelector
            type="representative"
            onSelect={handleSelectRepresentative}
            placeholder="Representante"
          />
        </div>
      </div>

      {/* Contexto actual */}
      <ChatContext
        selectedProduct={context.selectedProduct}
        selectedCustomer={context.selectedCustomer}
        selectedRepresentative={context.selectedRepresentative}
        onClearContext={handleClearContext}
        onClearProduct={handleClearProduct}
        onClearCustomer={handleClearCustomer}
        onClearRepresentative={handleClearRepresentative}
      />

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${
              message.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                message.role === "user"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 text-gray-900"
              }`}
            >
              {message.content}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="p-4 border-t">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={user ? "Escribe tu mensaje..." : "Inicia sesión para usar el chat"}
            disabled={loading || !n8nConfig || !user}
          />
          <Button type="submit" disabled={loading || !n8nConfig || !user}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
} 